import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import { FaRegCalendar } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Config from "../../Config";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    CardWrapper,
    SelectionCard,
    Wrapper,
    ScrollableContent,
    ActionSection,
    MobileIcon,
    ModalHeader,
    RupeeIcon,
} from "../Styles/InputStyles";
import CategoryCard from "../CategoryCard";
import tw from "tailwind-styled-components";
import SinglePropSelectModal from "../AddTenant/SinglePropSelectModal";
import SelectRoom from "../AddTenant/SelectRoom";

const AddBooking = ({ handleClose, addBookingLoading, addBookingMutate }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const propertyList = useSelector((state) => state.PropertyReducer.property);
    const formikRef = useRef(null);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [roomModalOpen, setRoomModalOpen] = useState(false);
    const [roomList, setRoomList] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [openCalendar, setOpenCalendar] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [selectedBed, setSelectedBed] = useState(null);
    const [showLongTermOptions, setShowLongTermOptions] = useState(false);

    const genderOptions = [
        { value: 1, label: "Male" },
        { value: 2, label: "Female" },
    ];

    const rentalTypeOptions = [
        { label: "Monthly", value: 1 },
        { label: "Short Stay", value: 2 },
        { label: "Long Term", value: 3 },
    ];

    const longTermOptions = [
        { label: "Quarterly", value: 3 },
        { label: "Half-Yearly", value: 6 },
        { label: "Annual", value: 12 },
    ];

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .required("Name is required")
            .matches(/^[A-Za-z0-9. ]{4,}$/, "Minimum 4 characters, letters, numbers, dot and spaces allowed"),
        mobile: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Enter a valid 10-digit mobile number"),
        gender: Yup.string()
            .required("Please select gender"),
        moveInDate: Yup.date()
            .required("Move-in date is required")
            .min(
                moment().add(1, "day").startOf("day").toDate(),
                "Move-in date must be from tomorrow onwards"
            ),
        checkoutDate: Yup.date()
            .nullable()
            .test(
                "checkout-date-required",
                "Check-out date is required for short stays",
                function (value) {
                    return this.parent.rentalType !== 2 || Boolean(value);
                }
            )
            .test(
                "checkout-after-movein",
                "Check-out date must be after check-in date",
                function (value) {
                    if (this.parent.rentalType !== 2) return true;

                    return (
                        !value ||
                        !this.parent.moveInDate ||
                        moment(value).isAfter(this.parent.moveInDate)
                    );
                }
            ),
        propId: Yup.string()
            .required("Please select a property"),
        roomId: Yup.string()
            .required("Please select a room"),
        rentalType: Yup.number()
            .required("Please select Rental Type")
            .oneOf([1, 2, 3, 6, 12], "Invalid rental type"),
        rent: Yup.number().when("rentalType", {
            is: 2,
            then: (schema) =>
                schema
                    .nullable()
                    .test(
                        "rent-range",
                        "Rent must be between ₹0 and ₹1,000,000",
                        (value) =>
                            value === undefined ||
                            value === null ||
                            value === "" ||
                            (value >= 0 && value <= 1000000)
                    ),
            otherwise: (schema) =>
                schema
                    .required("Rent is required")
                    .min(1000, "Rent must be between ₹1,000 and ₹1,000,000")
                    .max(1000000, "Rent must be between ₹1,000 and ₹1,000,000"),
        }),
        // rent: Yup.number()
        //     .required("Rent is required")
        //     .test(
        //         "rent-validation",
        //         function (value) {
        //             const { rentalType } = this.parent;

        //             if (rentalType === 2) {
        //                 return (
        //                     value >= 0 &&
        //                     value <= 1000000
        //                 ) || this.createError({
        //                     message: "Rent must be between ₹0 and ₹1,000,000",
        //                 });
        //             }

        //             return (
        //                 value >= 1000 &&
        //                 value <= 1000000
        //             ) || this.createError({
        //                 message: "Rent must be between ₹1,000 and ₹1,000,000",
        //             });
        //         }
        //     ),
        sendNotiToTenant: Yup.boolean(),
    });

    const moveInDate = moment().add(1, "day");
    const checkoutDate = moveInDate.clone().add(15, "days");

    const initialValues = {
        name: "",
        mobile: "",
        gender: 1,
        moveInDate: moveInDate.format("YYYY-MM-DD"),
        checkoutDate: checkoutDate.format("YYYY-MM-DD"),
        propId: "",
        roomId: "",
        rentalType: 1,
        rent: "",
        sendNotiToTenant: 0,
    };

    const fetchRooms = async () => {
        if (!selectedProperty) return;
        const response = await axios.get(
            `${Config.apiUrl}/room/available/list/${selectedProperty?.id}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
            }
        );
        return response.data;
    };

    const {
        isLoading: roomsLoading,
        data: roomData,
        refetch,
    } = useQuery(["roomData", selectedProperty], fetchRooms, {
        enabled: false,
        onSuccess: (data) => {
            if (data && data.data) {
                setRoomList(data.data);
            } else {
                setRoomList([]);
            }
        },
        onError: () => {
            setRoomList([]);
        },
    });

    useEffect(() => {
        setSelectedRoom(null);
        setRoomList([]);
        if (selectedProperty) {
            refetch();
        }
    }, [selectedProperty, refetch]);

    useEffect(() => {
        if (formikRef.current && selectedProperty) {
            formikRef.current.setFieldValue("propId", selectedProperty.id);
        }
    }, [selectedProperty]);

    useEffect(() => {
        if (formikRef.current && selectedRoom && selectedRoom.length > 0) {
            formikRef.current.setFieldValue("roomId", selectedRoom[0].id);
            formikRef.current.setFieldValue("rent", selectedRoom[0].roomPrice);
        }
    }, [selectedRoom]);

    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSubmit = (values, { resetForm }) => {
        if (!selectedProperty) {
            toast.error("Please select a property");
            return;
        }
        if (!selectedRoom || selectedRoom.length === 0) {
            toast.error("Please select a room");
            return;
        }

        if (values.rentalType === 2) {
            if (!values.checkoutDate) {
                toast.error("Check-out date is required for short stays");
                return;
            }
        }

        const payload = {
            propId: selectedProperty.id,
            roomId: selectedRoom[0]?.id,
            bedId: selectedBed?.id,
            moveInDate: values.moveInDate,
            name: values.name,
            mobile: values.mobile,
            gender: values.gender,
            rent: values.rent,
            stayType:
                values.rentalType === 1
                    ? 0
                    : values.rentalType === 2
                        ? 2
                        : 1,
            rentalType: values.rentalType,
            sendNotiToTenant: values.sendNotiToTenant ? 1 : 0,
        };

        if (values.rentalType === 2 && values.checkoutDate) {
            payload.moveOutDate = values.checkoutDate;
        }
        addBookingMutate(payload);
    };

    const handlePropertySelect = (propId) => {
        setSelectedProperty(propId);
        setSelectedRoom(null);
        if (formikRef.current) {
            formikRef.current.setFieldValue("propId", propId.id);
            formikRef.current.setFieldValue("roomId", "");
        }
        setShowPropertyModal(false);
    };

    const handleRoomSelect = (room, setFieldValue) => {
        if (room) {
            setFieldValue("roomId", room.id);
            setSelectedRoom([room]);
            setSelectedBed(room.beds?.[0] || null);
            setFieldValue("rent", room.roomPrice);
        } else {
            setFieldValue("roomId", "");
            setFieldValue("rent", "");
            setSelectedRoom(null);
            setSelectedBed(null);
        }
        setRoomModalOpen(false);
    };

    const renderSelectedRoom = (selected = []) => {
        if (!selected || !selected.length) return "Select Room";
        const room = selected[0];
        return room.roomNum || "Room";
    };

    const filteredProperties = propertyList.filter(
        (prop) => prop.id !== 0 && (prop.vacantBedCount || 0) > 0
    );

    return (
        <Container style={{ height: `${height}px`, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Booking</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={formikRef}
                    validateOnBlur={true}
                    validateOnChange={true}
                >
                    {({ values, setFieldValue, errors, touched }) => {
                        return (
                            <>
                                <Form>
                                    <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                        <Wrapper>
                                            {/* Name */}
                                            <InputGroup>
                                                <Label htmlFor="name">Name *</Label>
                                                <FieldWrappers>
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        id="name"
                                                        placeholder="Enter full name"
                                                        autoComplete="new-password"
                                                    />
                                                </FieldWrappers>
                                                {touched.name && errors.name && (
                                                    <p className="text-rose-600 text-sm">{errors.name}</p>
                                                )}
                                            </InputGroup>

                                            {/* Mobile */}
                                            <InputGroup>
                                                <Label htmlFor="mobile">Mobile *</Label>
                                                <FieldWrappers>
                                                    <MobileIcon />
                                                    <Field
                                                        type="text"
                                                        name="mobile"
                                                        id="mobile"
                                                        placeholder="Enter 10-digit mobile number"
                                                        inputMode="numeric"
                                                        maxLength={10}
                                                        autoComplete="new-password"
                                                        className="pl-6"
                                                    />
                                                </FieldWrappers>
                                                {touched.mobile && errors.mobile && (
                                                    <p className="text-rose-600 text-sm">{errors.mobile}</p>
                                                )}
                                            </InputGroup>

                                            {/* Gender */}
                                            <InputGroup>
                                                <Label>Gender *</Label>
                                                <CardWrapper>
                                                    {genderOptions.map((option) => (
                                                        <SelectionCard
                                                            key={option.value}
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setFieldValue("gender", option.value);
                                                            }}
                                                            className={
                                                                values.gender === option.value
                                                                    ? "bg-[#0078BD] text-white"
                                                                    : "bg-gray-100 text-gray-600"
                                                            }
                                                        >
                                                            <span className="text-sm">{option.label}</span>
                                                        </SelectionCard>
                                                    ))}
                                                </CardWrapper>
                                                {touched.gender && errors.gender && (
                                                    <p className="text-rose-600 text-sm">{errors.gender}</p>
                                                )}
                                            </InputGroup>

                                            {/* Property */}
                                            <InputGroup>
                                                <Label>Select Property *</Label>
                                                <CategoryCard
                                                    title="Property"
                                                    selectedCategory={selectedProperty}
                                                    onClick={() => setShowPropertyModal(true)}
                                                />
                                                {touched.propId && errors.propId && (
                                                    <p className="text-rose-600 text-sm mt-1">{errors.propId}</p>
                                                )}
                                            </InputGroup>

                                            {/* Room */}
                                            <InputGroup>
                                                <Label>Select Room *</Label>
                                                {selectedProperty ? (
                                                    roomList.length > 0 ? (
                                                        <>
                                                            <CategoryCard
                                                                title="Room"
                                                                selectedCategory={
                                                                    selectedRoom && selectedRoom.length > 0
                                                                        ? {
                                                                            label: renderSelectedRoom(selectedRoom),
                                                                            items: selectedRoom,
                                                                        }
                                                                        : null
                                                                }
                                                                onClick={() => setRoomModalOpen(true)}
                                                            />
                                                            {touched.roomId && errors.roomId && (
                                                                <p className="text-rose-600 text-sm mt-1">{errors.roomId}</p>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                            <p className="text-gray-500 text-sm">
                                                                {roomsLoading
                                                                    ? "Loading rooms..."
                                                                    : "No rooms available in this property"}
                                                            </p>
                                                        </div>
                                                    )
                                                ) : (
                                                    <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                        <p className="text-gray-500 text-sm">
                                                            Please select a property first
                                                        </p>
                                                    </div>
                                                )}
                                            </InputGroup>

                                            {/* Rental Type */}
                                            <InputGroup>
                                                <Label>Rental Type *</Label>
                                                <CardWrapper>
                                                    {rentalTypeOptions.map((mode) => (
                                                        <SelectionCard
                                                            key={mode.value}
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setFieldValue("rentalType", mode.value);
                                                                setShowLongTermOptions(mode.value === 3);

                                                                // if (mode.value === 2) {
                                                                //     setFieldValue("rent", 0);
                                                                //     setFieldValue("checkoutDate", null);
                                                                // }
                                                                if (mode.value === 2) {
                                                                    const moveInDate = moment().add(1, "day");
                                                                    const checkoutDate = moveInDate.clone().add(15, "days");

                                                                    setFieldValue("rent", 0);
                                                                    setFieldValue("checkoutDate", checkoutDate.format("YYYY-MM-DD"));
                                                                }
                                                                else {
                                                                    const room = selectedRoom?.[0];
                                                                    setFieldValue("rent", room?.roomPrice || "");
                                                                    setFieldValue("checkoutDate", null);
                                                                }
                                                            }}
                                                            className={values.rentalType === mode.value
                                                                ? "bg-[#0078BD] text-white"
                                                                : "bg-gray-100 text-gray-600"}
                                                        >
                                                            <span className="text-sm">{mode.label}</span>
                                                        </SelectionCard>
                                                    ))}
                                                </CardWrapper>
                                                {touched.rentalType && errors.rentalType && (
                                                    <p className="text-rose-600 text-sm">
                                                        {errors.rentalType}
                                                    </p>
                                                )}
                                                {showLongTermOptions && (
                                                    <CardWrapper className="mt-2">
                                                        {longTermOptions.map((mode) => (
                                                            <SelectionCard
                                                                key={mode.value}
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setFieldValue("rentalType", mode.value);
                                                                }}
                                                                className={values.rentalType === mode.value
                                                                    ? "bg-[#0078BD] text-white"
                                                                    : "bg-gray-100 text-gray-600"}
                                                            >
                                                                <span className="text-sm">{mode.label}</span>
                                                            </SelectionCard>
                                                        ))}
                                                    </CardWrapper>
                                                )}
                                            </InputGroup>

                                            {/* Rent */}
                                            <InputGroup>
                                                <Label htmlFor="rent">
                                                    {values.rentalType === 1
                                                        ? "Monthly Rent *"
                                                        : values.rentalType === 2
                                                            ? "Per Day Rent *"
                                                            : values.rentalType === 3
                                                                ? "Quarterly Rent *"
                                                                : values.rentalType === 6
                                                                    ? "Half-Yearly Rent *"
                                                                    : values.rentalType === 12
                                                                        ? "Annual Rent *"
                                                                        : "Rent *"}
                                                </Label>
                                                <FieldWrappers>
                                                    <RupeeIcon />
                                                    <Field
                                                        type="number"
                                                        name="rent"
                                                        id="rent"
                                                        placeholder="Enter rent amount"
                                                        className="pl-6 w-full"
                                                        autoComplete="off"
                                                        onFocus={() => {
                                                            if (values.rent === 0) {
                                                                setFieldValue("rent", "");
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            if (values.rentalType === 2 && e.target.value === "") {
                                                                setFieldValue("rent", 0);
                                                            }
                                                        }}
                                                    />
                                                </FieldWrappers>
                                                {touched.rent && errors.rent && (
                                                    <p className="text-rose-600 text-sm">{errors.rent}</p>
                                                )}
                                            </InputGroup>

                                            {/* Move-in Date */}
                                            <InputGroup>
                                                <Label htmlFor="moveInDate">
                                                    {values.rentalType === Config.RENTAL_OPTIONS.SHORT_DURATION ? "Check-in Date *" : "Move-In Date *"}
                                                </Label>
                                                <FieldWrappers>
                                                    <DatePicker
                                                        selected={values.moveInDate ? moment(values.moveInDate, "YYYY-MM-DD").toDate() : null}
                                                        onChange={(date) => {
                                                            setFieldValue("moveInDate", moment(date).format("YYYY-MM-DD"));
                                                            setOpenCalendar(null);
                                                        }}
                                                        onClickOutside={() => setOpenCalendar(null)}
                                                        onSelect={() => setOpenCalendar(null)}
                                                        open={openCalendar === 'moveIn'}
                                                        onInputClick={() => {
                                                            setOpenCalendar(openCalendar === 'moveIn' ? null : 'moveIn');
                                                        }}
                                                        dateFormat="yyyy-MM-dd"
                                                        className="cursor-pointer items-center w-full"
                                                        placeholderText="YYYY-MM-DD"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        minDate={moment().add(1, "day").toDate()}
                                                        onFocus={(e) => e.target.blur()}
                                                        wrapperClassName="w-full"
                                                        name="moveInDate"
                                                        id="moveInDate"
                                                        autoComplete="off"
                                                        portalId="root-portal"
                                                        popperClassName="react-datepicker-popper"
                                                    />
                                                </FieldWrappers>
                                                {touched.moveInDate && errors.moveInDate && (
                                                    <p className="text-rose-600 text-sm">{errors.moveInDate}</p>
                                                )}
                                            </InputGroup>

                                            {/* Check-out Date - Only for Short Stay */}
                                            {values.rentalType === Config.RENTAL_OPTIONS.SHORT_DURATION && (
                                                <InputGroup>
                                                    <Label htmlFor="checkoutDate">Check-out Date *</Label>
                                                    <FieldWrappers>
                                                        <DatePicker
                                                            selected={values.checkoutDate ? moment(values.checkoutDate, "YYYY-MM-DD").toDate() : null}
                                                            onChange={(date) => {
                                                                setFieldValue("checkoutDate", moment(date).format("YYYY-MM-DD"));
                                                                setOpenCalendar(null);
                                                            }}
                                                            onClickOutside={() => setOpenCalendar(null)}
                                                            onSelect={() => setOpenCalendar(null)}
                                                            open={openCalendar === 'checkout'}
                                                            onInputClick={() => {
                                                                setOpenCalendar(openCalendar === 'checkout' ? null : 'checkout');
                                                            }}
                                                            dateFormat="yyyy-MM-dd"
                                                            className="cursor-pointer items-center w-full"
                                                            placeholderText="YYYY-MM-DD"
                                                            showIcon
                                                            icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                            showPopperArrow={false}
                                                            minDate={values.moveInDate ? moment(values.moveInDate).add(1, 'days').toDate() : null}
                                                            onFocus={(e) => e.target.blur()}
                                                            wrapperClassName="w-full"
                                                            name="checkoutDate"
                                                            id="checkoutDate"
                                                            autoComplete="off"
                                                            portalId="root-portal"
                                                            popperClassName="react-datepicker-popper"
                                                        />
                                                    </FieldWrappers>
                                                    {touched.checkoutDate && errors.checkoutDate && (
                                                        <p className="text-rose-600 text-sm">
                                                            {errors.checkoutDate}
                                                        </p>
                                                    )}
                                                </InputGroup>
                                            )}

                                            {/* Send Notification to Tenant - Checkbox */}
                                            <InputGroup>
                                                <Label className="flex items-center space-x-2 cursor-pointer">
                                                    <Field
                                                        type="checkbox"
                                                        name="sendNotiToTenant"
                                                        id="sendNotiToTenant"
                                                        checked={values.sendNotiToTenant === 1}
                                                        onChange={(e) => {
                                                            setFieldValue("sendNotiToTenant", e.target.checked ? 1 : 0);
                                                        }}
                                                        className="w-4 h-4 text-primary-color form-checkbox accent-primary-color"
                                                    />
                                                    <span className="text-sm text-gray-800">
                                                        Send notification to tenant
                                                    </span>
                                                </Label>
                                                {touched.sendNotiToTenant && errors.sendNotiToTenant && (
                                                    <p className="text-rose-600 text-sm">{errors.sendNotiToTenant}</p>
                                                )}
                                            </InputGroup>

                                        </Wrapper>
                                    </ScrollableContent>

                                    <ActionSection>
                                        <SideCancelBtn type="button" onClick={handleClose}>
                                            Cancel
                                        </SideCancelBtn>
                                        <SubmitBtn
                                            type="submit"
                                            disabled={addBookingLoading || roomsLoading || !selectedRoom}
                                        >
                                            {addBookingLoading ? "Submitting..." : "Submit"}
                                        </SubmitBtn>
                                    </ActionSection>

                                    {/* Property Selection Modal */}
                                    <SinglePropSelectModal
                                        isOpen={showPropertyModal}
                                        onClose={() => setShowPropertyModal(false)}
                                        onSelect={handlePropertySelect}
                                        options={filteredProperties}
                                        selectedProperty={selectedProperty}
                                    />

                                    {/* Room Selection Modal */}
                                    <SelectRoom
                                        isOpen={roomModalOpen}
                                        onClose={() => setRoomModalOpen(false)}
                                        onSelect={(room) => handleRoomSelect(room, setFieldValue)}
                                        options={roomList}
                                        selectedItem={selectedRoom}
                                        title="Select Room"
                                        searchPlaceholder="Search rooms..."
                                        idKey="id"
                                        nameKey="roomNum"
                                        descriptionKeys={["amenities"]}
                                        emptyMessage="No rooms available"
                                        noResultsMessage="No rooms found"
                                        showClearSelection={true}
                                    />
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;

export default AddBooking;